import styled from '@emotion/styled'
import { concat, includes, without } from 'lodash'
import { LOCIZE_NAMESPACE } from 'main/common'
import { useWhitelabel } from 'main/framework'
import { i18nKeyPrefix } from 'main/pages/Search/i18n'
import { breakpoints, Checkbox, Hidden, spacing, Strong, Text, Visible } from 'main/ui'
import { IFilters } from 'main/util/search'
import * as React from 'react'
import { useTranslation } from 'react-i18next'

import {
  Apply,
  CheckboxGroupContainer,
  Clear,
  FieldCopyContainer,
  FieldsContainer,
  FilterFormContainer as CommonFilterFormContainer,
  FilterFormSubmitContainer,
} from '../../common/FilterForm.styles'
import { ISearchFormProps } from '../../types'

import {
  LinkWithLeftPaddingSometimes,
  NetworkRatingLabel,
  SpaceBetweenFlexSpan,
  Stars,
} from './FilterForm.styles'

const FilterFormContainer = styled(CommonFilterFormContainer)`
  @media (min-width: ${breakpoints.lg}px) {
    padding: ${spacing.majorScale(3)}px ${spacing.majorScale(4)}px ${spacing.majorScale(4)}px;
    max-width: 310px;
  }
`
const PRE = `${i18nKeyPrefix}-FILTERS-NETWORK-RATING-V2`

export default function FilterForm(props: ISearchFormProps) {
  const { filters, onChangeFilters, onRequestCloseForm } = props
  const { t } = useTranslation(LOCIZE_NAMESPACE)

  const [params, setParams] = React.useState<IFilters>({ ...filters })

  function handleApply() {
    onRequestCloseForm()
    onChangeFilters({ ...filters, selectedNetworkRatingsV2: params.selectedNetworkRatingsV2 })
  }
  function handleClear() {
    setParams({ ...params, selectedNetworkRatingsV2: [] })
  }

  return (
    <FilterFormContainer>
      <Fields params={params} onChangeParams={setParams} />
      <Visible lg xl>
        <FilterFormSubmitContainer css={{ marginTop: spacing.minorScale(5) }}>
          <Clear onClick={() => handleClear()}>{t(`${PRE}-CLEAR`)}</Clear>
          <Apply onClick={() => handleApply()}>{t(`${PRE}-APPLY`)}</Apply>
        </FilterFormSubmitContainer>
      </Visible>
    </FilterFormContainer>
  )
}

export function Fields(fieldsProps: {
  params: IFilters
  onChangeParams: (params: IFilters) => void
}) {
  const { params, onChangeParams } = fieldsProps
  const { t } = useTranslation(LOCIZE_NAMESPACE)
  const whitelabel = useWhitelabel()
  const networkRatingData = whitelabel.networkRatingData()
  const learnMoreLink = networkRatingData.ratingLearnMore

  return (
    <div>
      <Visible lg xl>
        <SpaceBetweenFlexSpan>
          <Strong>
            {networkRatingData.ratingSystemSubtitle && t(networkRatingData.ratingSystemSubtitle)}
          </Strong>
          <LinkWithLeftPaddingSometimes href={learnMoreLink} target="_blank">
            {t(`${PRE}-MORE`)}
          </LinkWithLeftPaddingSometimes>
        </SpaceBetweenFlexSpan>
      </Visible>
      <Hidden lg xl>
        <FieldCopyContainer>
          <Text>
            <Strong>
              {networkRatingData.ratingSystemSubtitle && t(networkRatingData.ratingSystemSubtitle)}
            </Strong>
            <div>
              <LinkWithLeftPaddingSometimes href={learnMoreLink} target="_blank">
                {t(`${PRE}-MORE`)}
              </LinkWithLeftPaddingSometimes>
            </div>
          </Text>
        </FieldCopyContainer>
      </Hidden>
      <FieldsContainer>
        <CheckboxGroupContainer css={{ width: 'max-content' }}>
          {networkRatingData.filterOptions.map(option => {
            return (
              <Checkbox
                key={option.label}
                checked={includes(params.selectedNetworkRatingsV2, option.slug)}
                label={
                  <NetworkRatingLabel>
                    <span>{option.label}</span>
                    <Stars>{networkRatingData.stars(option.symbolCount)}</Stars>
                  </NetworkRatingLabel>
                }
                onChange={(checked: boolean) => {
                  checked
                    ? onChangeParams({
                        ...params,
                        selectedNetworkRatingsV2: concat(
                          params.selectedNetworkRatingsV2,
                          option.slug,
                        ),
                      })
                    : onChangeParams({
                        ...params,
                        selectedNetworkRatingsV2: without(
                          params.selectedNetworkRatingsV2,
                          option.slug,
                        ),
                      })
                }}
              />
            )
          })}
        </CheckboxGroupContainer>
      </FieldsContainer>
    </div>
  )
}
